import { CITIES, cityById, shuffledCityIds, type City } from '@/config/cities';
import {
  LEGACY_VEHICLE_UNLOCKS_M62,
  legacyUnlockThresholdFor,
  VEHICLE_ROSTER,
  vehicleUnlockedBy,
} from '@/config/racers';
import {
  cosmeticById,
  isCosmeticId,
  isKitNumber,
  isTifoStyle,
  type WireLoadout,
} from '@/config/cosmetics';
import { pointsForPlace } from '@/core/Progression';
import { asDifficulty, type DifficultyLevel } from '@/config/tuning';
import { TEASER_FREE_RACES } from '@/config/entitlement';

/**
 * SaveData.ts — the on-device save file (pure logic + a localStorage wrapper).
 *
 * One versioned JSON blob holds everything that persists between sessions:
 * settings, the active Tour, and a per-city record (best place / best time /
 * unlocked). The owner-approved progression model (2026-06-11):
 *
 *   - The Tour ALWAYS advances to the next city, win or lose.
 *   - Each race awards championship points by finish place (see Progression.ts).
 *   - Finishing a city in the Tour unlocks it for Quick Race.
 *   - The first city (NYC) is always unlocked.
 *
 * All mutation helpers are PURE (they return a new save object) so they're
 * unit-testable; only `loadSave`/`storeSave` touch the browser.
 */

/** Player-tunable settings (wired up by the M10 settings screen). */
export interface Settings {
  master: number; // 0..1 master volume
  sfx: number; // 0..1 sound-effect volume
  music: number; // 0..1 music volume
  /** M88 — hard mute for ALL audio (the title speaker button). The volume
   *  sliders keep their values; unmuting restores them exactly. */
  muted: boolean;
  quality: 'auto' | 'low' | 'medium' | 'high';
  reducedMotion: boolean; // no screen shake / FOV punch
  hudScale: number; // 0.85..1.3 multiplier on HUD text/controls
  /** UNUSED since M87 (owner rule: every offline race is you + 6 CPU rivals —
   *  the old 3/5/7 chips are gone). Kept so stored blobs keep their shape. */
  rivals: number;
  /** AI rival difficulty for EVERY offline race — Quick Race AND Tour (owner
   *  rule, 2026-07-15: one persistent setting until the player changes it; it
   *  replaced the per-city tour ramp + the Quick Race AUTO override). Online
   *  races ignore it — the server pins all online AI to legendary. */
  difficulty: DifficultyLevel;
  /** Left-handed touch layout (M31): joystick on the RIGHT, item button on the
   *  LEFT (mirrors the default right-handed layout). Touch controls only. */
  lefty: boolean;
  /** The player's chosen vehicle id (M35 car-select). One of the vehicles in
   *  src/entities/vehicles.ts; applies to the player's car in every offline mode. */
  playerVehicle: string;
  /** Touch steering-sensitivity multiplier (0.5..1.5) on tuning.touch.steerSensitivity:
   *  higher = a smaller thumb sweep reaches full lock (less reach/strain). Touch only. */
  touchSteerSensitivity: number;
  /** Touch steering scheme. M115 (owner): 'tilt' is RETIRED as a player option —
   *  it never steered well enough to ship — so this is always 'joystick' now
   *  (parseSave coerces stored 'tilt' blobs). The union keeps 'tilt' so the save
   *  shape is unchanged and the parked gyro code (src/input/Tilt.ts) can come
   *  back in a future round without a migration. */
  controlScheme: 'joystick' | 'tilt';
}

/** One race's outcome inside a Tour: every racer's place, by racer id. */
export interface TourResult {
  cityId: string;
  /** places[racerId] = finish place 1..3 (standings order when the player finished). */
  places: number[];
  /** The player's race time in ms (null if they somehow didn't finish). */
  timeMs: number | null;
}

/** An in-progress (or just-finished) Tour. The next city index = results.length. */
export interface TourSave {
  results: TourResult[];
  /** M87 — THIS tour's city order (every city id exactly once, rolled fresh per
   *  new tour). results[i] was raced at order[i]; the next stop is
   *  order[results.length]. Pre-M87 saves backfill the canonical CITIES order
   *  at the load boundary, so an old mid-tour save continues unchanged. */
  order: string[];
}

/** What the player has achieved in one city (across all modes). */
export interface CityRecord {
  unlocked: boolean;
  /** Best (lowest) finish place, 0 = never finished a race here. */
  bestPlace: number;
  /** Fastest player finish in ms, null until they finish a race here. */
  bestTimeMs: number | null;
  /** How many races they've completed here (any mode). */
  raced: number;
}

/** M79 — one vehicle's equipped cosmetics (item ids from config/cosmetics).
 *  M128: `mods` (any number of mod ids, they stack) + `kitNumber` (1-11) +
 *  M128c `tifoStyle` (1-3, the Fan Tifo cape's design). The
 *  shape is the wire shape too, so it lives beside the codec in cosmetics. */
export type VehicleLoadout = WireLoadout;

/** M79 — the player's shop state: items owned (account-wide) + what each
 *  vehicle is wearing. Purchases NEVER expire and equipping is free. */
export interface CosmeticsSave {
  owned: string[];
  loadouts: Record<string, VehicleLoadout>;
}

/** Nickname length cap (M80 title profile block — fits the card at every size). */
export const NICKNAME_MAX = 12;

// ---------------------------------------------------------------------------
// M100 RACER ID — the cloud-account fields. The account itself lives in
// Supabase (auth + the point_events/purchases ledgers); the save only carries
// the DEVICE's view of it: which account this device is linked to, the
// signed-out "guest pot", and the offline outboxes waiting to sync.
// ---------------------------------------------------------------------------

/** Signed-out Skill-Point earnings wait here until a Racer ID claims them.
 *  Mirrors the server's guest_claim cap schedule (300/day), so the pot is
 *  bounded — a full pot is itself the strongest "create your Racer ID" nudge. */
export const GUEST_POT_CAP = 1000;
/** Outbox bounds — far above anything reachable in real offline play. */
export const PENDING_AWARDS_CAP = 400;
export const PENDING_PURCHASES_CAP = 120;
/** M123 — storage bound on the teaser-race counter (the guestPoints idiom:
 *  a corrupt blob can only ever land inside [0, cap]). The MEANINGFUL
 *  threshold is TEASER_FREE_RACES (config/entitlement) — this cap only stops
 *  a hand-edited blob storing garbage; the counter has no reason to grow
 *  past it because the soft cap fired long before. */
export const TEASER_RACES_CAP = 999;

/** One queued Skill-Point award waiting to post to the account (linked saves
 *  only). `id` is the idempotency key — the server dedupes replays on it, so
 *  a flush retry can never double-award. `points` is what the client banked
 *  locally (display/reconcile only — the SERVER recomputes from kind+place). */
export interface PendingAward {
  id: string;
  kind: 'race' | 'tour_finale_offline' | 'tour_finale_online';
  place: number;
  points: number;
  /** etWeekKey at earn time — lets reconcile rebuild the weekly counter. */
  week: string;
}

/** The account this DEVICE is linked to (null = guest mode). */
export interface AccountSave {
  userId: string;
}

/** Trim/cap a raw nickname and strip control characters; '' = unset (the UI
 *  shows the RACER default). The ONE normalizer — used by the load boundary
 *  and the title screen's edit field. */
export function sanitizeNickname(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return [...raw]
    .filter((ch) => {
      const cp = ch.codePointAt(0) ?? 0;
      return cp >= 0x20 && cp !== 0x7f;
    })
    .join('')
    .trim()
    .slice(0, NICKNAME_MAX);
}

export interface SaveData {
  version: 1;
  settings: Settings;
  tour: TourSave | null;
  /** M80 — the player's chosen display name ('' until they set one; local-only
   *  until accounts land, then it becomes their handle). */
  nickname: string;
  cities: Record<string, CityRecord>;
  /** M62 — lifetime CAREER Skill Points across ALL races — offline Quick +
   *  Tour, and (M63) online friend rooms / Quick Match / online tours. Grows
   *  only; drives vehicle unlocks (config `VEHICLE_UNLOCKS`), skill tiers and
   *  the leaderboard. Never on the online wire (each client awards its own
   *  save from its own results). M79: spending happens via `spentPoints`
   *  below so unlocks/rank can never regress — see core/skillPoints.ts. */
  lifetimePoints: number;
  /** M114 — vehicles GRANTED FOREVER, regardless of what the current
   *  VEHICLE_UNLOCKS thresholds say. Fed two ways: every threshold crossing
   *  (skillPoints.applySkillAward) appends here, and a pre-M114 blob seeds it
   *  from the LEGACY ladder at parse — so raising unlock costs (the M114
   *  retune, or any future one) can never re-lock a car a player earned.
   *  Never contains 'coupe' (always free) or unknown ids (parseSave drops
   *  them). The one unlock read is config `vehicleUnlockedBy`. */
  unlockedVehicles: string[];
  /** M79 — career points SPENT in the shop. Balance = lifetime + purchased - spent. */
  spentPoints: number;
  /**
   * M130 — Skill Points BOUGHT with money (the SP packs). Spendable in the
   * shop like earned SP, but NEVER part of lifetimePoints: bought SP cannot
   * unlock a vehicle, move a skill tier or rank on a leaderboard (the owner's
   * one rule). The SERVER is the source of truth (profiles.purchased_points,
   * credited only by the payment webhooks) — this is the account's mirror,
   * adopted wholesale on every sync; a guest device can never accrue any.
   * May legitimately read below what was bought after a refund of SP that was
   * already spent (the bank shows 0 until earnings repay it).
   */
  purchasedPoints: number;
  /** M79 — the highest OFFLINE tour championship bonus already paid (owner
   *  rule: offline tours pay the finale bonus once — upgrades pay only the
   *  difference; online tours pay every time). */
  offlineTourBonusPaid: number;
  /** M79 — shop purchases + per-vehicle equipped looks. */
  cosmetics: CosmeticsSave;
  /** M82 — Skill Points earned THIS WEEK (the This-Week leaderboard). `key`
   *  is the ET-Monday week stamp (core/skillPoints etWeekKey); a mismatched
   *  key means the week rolled over and the counter reads as 0. */
  weekly: { key: string; points: number };
  /** M100 — the Racer ID this device is linked to (null = guest mode). */
  account: AccountSave | null;
  /** M100 — STICKY: this device's pre-account career was already imported
   *  into an account once. Survives sign-out/unlink so the same local points
   *  can never be imported into a second account (double-count guard). */
  accountImported: boolean;
  /** M103 — the LAST Racer ID this device was linked to. Unlike `account`
   *  it SURVIVES sign-out, so the sync engine can tell "the same person
   *  re-linking" from "a DIFFERENT person signed in" across a sign-out gap —
   *  the latter must adopt the new account's server state wholesale instead
   *  of leaking this device's previous nickname/cosmetics into it. */
  lastAccountId: string | null;
  /** M100 — signed-out Skill-Point earnings waiting to be claimed. */
  guestPoints: number;
  /**
   * M121 — OFFLINE GRACE ONLY. The last server answer to "does this account
   * own the base game", cached so a paying player who boots without a network
   * is not shown a paywall for a game they bought.
   *
   * This is NOT the source of truth and must never be treated as one. A live
   * server `false` always beats a cached `true` (that is how a refund takes
   * effect), and the cache only ever fills SILENCE. It is trivially editable
   * in localStorage — accepted, because the offline single-player content is
   * client-side and unprotectable anyway, and ONLINE play stays gated on the
   * server, which is where the real cost lives.
   *
   * CRITICAL: cleared on explicit sign-out and on a user switch. Without that
   * it stops meaning "the session could not refresh" and starts meaning "this
   * device remembers SOMEONE owned it" — a free game for the next person who
   * signs in here (the M103 never-leak rule, applied to the entitlement).
   */
  ownsGameCached: boolean;
  /**
   * M123 — how many free TEASER races this device has FINISHED (the paid
   * gate's soft replay cap). A non-owner's QUICK RACE launches the teaser
   * directly; each finished teaser race ticks this, and at/after
   * TEASER_FREE_RACES the next tap fronts the paywall instead (soft — the
   * paywall still offers "play the free race"). Device-local and trivially
   * resettable (incognito) — accepted, it is a nudge, not a wall. Never
   * incremented by the How-To-Play demo, tour, or online races.
   */
  teaserRaces: number;
  /** M100 — awards banked locally while linked, waiting to post (offline /
   *  expired-session outbox; flushed whenever a session is live). */
  pendingAwards: PendingAward[];
  /** M100 — shop purchases waiting to post (item ids; idempotent by item). */
  pendingPurchases: string[];
  /**
   * M109 — this device has GARAGE CHANGES the account hasn't seen yet.
   *
   * Owned items were always account-wide, but what each car WEARS lived only
   * on the device, so one Racer ID showed different paint jobs on every phone.
   * The garage is server-side now (profiles.loadouts + set_garage), and this
   * flag is the whole conflict rule: a device that changed something PUSHES on
   * its next sync; a device that didn't ADOPTS the account's garage. Set it
   * only when a value actually changes, so merely opening the garage on a
   * second device can't overwrite the first.
   */
  garageDirty: boolean;
  /** M114 — the one-time FIRST-WIN difficulty CTA (results panel) was already
   *  offered. Latched TRUE whatever the player chose; parseSave backfills
   *  TRUE for pre-M114 saves that already hold a win anywhere, so only a
   *  genuinely NEW first win on the default difficulty ever shows it. */
  difficultyCtaShown: boolean;
  /** M138 — one-time migration latch: this device's SIGNED-OUT state has
   *  been scrubbed of the last account's data (see toGuestSave). Before M138
   *  sign-out only unlinked, so a signed-out browser kept showing the
   *  account's cars, XP and SP. Every sign-out now scrubs on the spot; this
   *  latch lets the loader clean a device that signed out BEFORE the fix,
   *  exactly once. */
  signOutScrubbed: boolean;
}

export function defaultSettings(): Settings {
  return {
    master: 1,
    sfx: 1,
    music: 0.8,
    muted: false,
    quality: 'auto',
    reducedMotion: false,
    hudScale: 1,
    rivals: 3,
    difficulty: 'medium',
    lefty: false,
    playerVehicle: 'coupe', // M62: the starter ride every player begins with
    touchSteerSensitivity: 1,
    controlScheme: 'joystick',
  };
}

export function defaultSave(): SaveData {
  return {
    version: 1,
    settings: defaultSettings(),
    tour: null,
    nickname: '',
    cities: {},
    lifetimePoints: 0,
    unlockedVehicles: [],
    spentPoints: 0,
    purchasedPoints: 0,
    offlineTourBonusPaid: 0,
    cosmetics: { owned: [], loadouts: {} },
    weekly: { key: '', points: 0 },
    account: null,
    accountImported: false,
    lastAccountId: null,
    guestPoints: 0,
    ownsGameCached: false,
    teaserRaces: 0,
    pendingAwards: [],
    pendingPurchases: [],
    garageDirty: false,
    difficultyCtaShown: false,
    signOutScrubbed: true, // a fresh save has nothing to scrub
  };
}

/**
 * M138 (owner: "when logged out, the garage still shows my cars, XP and
 * SP") — the device as a GUEST after a sign-out.
 *
 * Everything the ACCOUNT owns and the server restores on the next sign-in is
 * reset: career XP, bought / spent SP, the weekly counter, owned cosmetics
 * and what each car wears, granted rides, the selected ride, city records,
 * the username. A shared computer must not keep showing (or racing with)
 * the last person's career, and every surface — garage, shop, title, the
 * car on the grid — then agrees, because they all read the same save.
 *
 * KEPT, deliberately: device settings (volumes, controls, difficulty), the
 * device's own tour progress (it is not on the server — wiping it would
 * destroy real progress), the guest pot, the teaser-race count, the
 * one-time flags, `lastAccountId` (M103 user-switch detection) and the
 * award / purchase OUTBOXES (M110: a same-user sign-in still flushes them;
 * a different user's sign-in drops them at the switch step). Nothing is
 * lost for the account: `reconcileWallet` rebuilds the wallet as server +
 * pending, the M109 garage sync adopts the account's garage onto a bare
 * device, and the M110 city-record merge brings the records back.
 */
export function toGuestSave(save: SaveData): SaveData {
  const fresh = defaultSave();
  return {
    ...structuredClone(save),
    settings: { ...structuredClone(save.settings), playerVehicle: 'coupe' },
    nickname: fresh.nickname,
    cities: fresh.cities,
    lifetimePoints: 0,
    unlockedVehicles: [],
    spentPoints: 0,
    purchasedPoints: 0,
    offlineTourBonusPaid: 0,
    cosmetics: fresh.cosmetics,
    weekly: fresh.weekly,
    account: null,
    ownsGameCached: false,
    garageDirty: false,
    signOutScrubbed: true,
  };
}

/** The record for a city (a fresh default if the player has never raced it). */
export function cityRecord(save: SaveData, cityId: string): CityRecord {
  return save.cities[cityId] ?? { unlocked: false, bestPlace: 0, bestTimeMs: null, raced: 0 };
}

/** Quick Race availability: the first city is always open; the rest unlock by
 *  finishing them in the Tour. */
export function isCityUnlocked(save: SaveData, cityId: string): boolean {
  if (CITIES.length > 0 && CITIES[0].id === cityId) return true;
  return cityRecord(save, cityId).unlocked;
}

/** Start (or restart) a Tour — a FRESH random city order every time (M87,
 *  owner rule). Continuing a saved tour keeps the order it was rolled with. */
export function startTour(save: SaveData, order: string[] = shuffledCityIds()): SaveData {
  const next = structuredClone(save);
  next.tour = { results: [], order: [...order] };
  return next;
}

/** The City at tour stop `idx` of THIS save's rolled order (canonical order as
 *  the safety fallback — parseSave normally guarantees a valid order). */
export function tourCityAt(save: SaveData, idx: number): City {
  return cityById(save.tour?.order?.[idx] ?? CITIES[idx]?.id ?? CITIES[0].id);
}

/** The index of the next Tour city (0-based into CITIES), or null if no tour /
 *  the tour is complete. */
export function tourCityIndex(save: SaveData): number | null {
  if (!save.tour) return null;
  const idx = save.tour.results.length;
  return idx < CITIES.length ? idx : null;
}

export function isTourComplete(save: SaveData): boolean {
  return !!save.tour && save.tour.results.length >= CITIES.length;
}

export interface RaceOutcome {
  cityId: string;
  places: number[]; // places[racerId] = 1..3
  playerTimeMs: number | null;
  mode: 'quick' | 'tour';
}

/**
 * Record a finished race: update the city's bests, bump its race count, and —
 * in Tour mode — append the tour result and unlock the city for Quick Race.
 */
export function recordRaceResult(save: SaveData, outcome: RaceOutcome): SaveData {
  const next = structuredClone(save);
  const rec = next.cities[outcome.cityId] ?? {
    unlocked: false,
    bestPlace: 0,
    bestTimeMs: null,
    raced: 0,
  };
  const playerPlace = outcome.places[0];
  rec.raced += 1;
  if (playerPlace > 0 && (rec.bestPlace === 0 || playerPlace < rec.bestPlace)) {
    rec.bestPlace = playerPlace;
  }
  if (outcome.playerTimeMs !== null) {
    rec.bestTimeMs =
      rec.bestTimeMs === null
        ? outcome.playerTimeMs
        : Math.min(rec.bestTimeMs, outcome.playerTimeMs);
  }
  if (outcome.mode === 'tour') {
    rec.unlocked = true;
    if (next.tour) {
      next.tour.results.push({
        cityId: outcome.cityId,
        places: [...outcome.places],
        timeMs: outcome.playerTimeMs,
      });
    }
  }
  next.cities[outcome.cityId] = rec;
  return next;
}

// ---------------------------------------------------------------------------
// M123 — the teaser soft cap (pure helpers; the paid gate's free-race economy).
// ---------------------------------------------------------------------------

/** One finished TEASER race — tick the counter (bounded; see TEASER_RACES_CAP).
 *  Pure like every other mutation helper here. Callers: exactly one — the
 *  offline branch of Flow.onRaceFinished, and ONLY when the finished race was
 *  launched as a teaser (the How-To-Play demo returns earlier and is uncapped
 *  by construction; online races never reach that branch). */
export function recordTeaserRace(save: SaveData): SaveData {
  const next = structuredClone(save);
  next.teaserRaces = Math.min(next.teaserRaces + 1, TEASER_RACES_CAP);
  return next;
}

/** Free teaser races this device still has before the soft cap (never
 *  negative). 0 = the next non-owner QUICK RACE tap fronts the paywall
 *  (context 'cap') instead of launching — though the paywall's own
 *  "play the free race" escape keeps racing possible (soft, not hard). */
export function teaserRacesLeft(save: SaveData): number {
  return Math.max(0, TEASER_FREE_RACES - save.teaserRaces);
}

// ---------------------------------------------------------------------------
// Browser storage wrapper (the only non-pure part).
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'dribble-racer.save.v1';

/** Parse + sanity-check a stored blob; anything broken falls back to defaults. */
export function parseSave(json: string | null): SaveData {
  if (!json) return defaultSave();
  try {
    const data = JSON.parse(json) as SaveData;
    if (!data || data.version !== 1 || typeof data.cities !== 'object' || data.cities === null) {
      return defaultSave();
    }
    // Backfill settings so older blobs survive new fields.
    data.settings = { ...defaultSettings(), ...data.settings };
    // M87: a tour carries its own rolled city order. A pre-M87 blob (no order)
    // gets the canonical CITIES order so an in-progress tour continues exactly
    // as it would have; anything malformed (wrong length, unknown/duplicate
    // ids) is coerced the same way rather than crashing city lookups.
    const rawTour = (data as { tour?: unknown }).tour as Partial<TourSave> | null | undefined;
    if (rawTour && typeof rawTour === 'object' && Array.isArray(rawTour.results)) {
      const ids = CITIES.map((c) => c.id);
      const order = Array.isArray(rawTour.order) ? rawTour.order : [];
      const valid =
        order.length === ids.length &&
        new Set(order).size === ids.length &&
        order.every((id) => typeof id === 'string' && ids.includes(id));
      data.tour = { results: rawTour.results, order: valid ? [...(order as string[])] : ids };
    } else {
      data.tour = null;
    }
    // M80: nickname — sanitize whatever is stored (pre-M80 blobs backfill '').
    data.nickname = sanitizeNickname((data as { nickname?: unknown }).nickname);
    // An invalid difficulty string would flow straight into aiCarParamsFor()
    // (a levels-table lookup) and crash the sim — coerce at the load boundary.
    data.settings.difficulty = asDifficulty(data.settings.difficulty);
    // M115 (owner): TILT steering was removed as an option. The field stays in
    // the save shape (no migration needed, and the gyro idea is parked in
    // src/input/Tilt.ts for a future round) but a save that already says 'tilt'
    // is coerced here — otherwise an existing tester would be left steering
    // with a scheme that has no UI to turn off.
    data.settings.controlScheme = 'joystick';
    // M62: lifetimePoints is a NEW top-level field — parseSave only spread-merges
    // SETTINGS, so backfill it explicitly (typeof guard, not `??`, so a real 0 in
    // a post-M62 save is preserved). For a pre-M62 blob (field absent) SEED from
    // prior progress so the player keeps the cars their finishes earned, and
    // GRANDFATHER their current ride so migration never strips a vehicle they
    // were already driving. (A malformed blob throws → caught → defaultSave.)
    // M114: the ride seed reads the LEGACY ladder deliberately — every point a
    // pre-M62 save could have earned was earned against the OLD thresholds, and
    // seeding to the raised M114 costs would gift phantom career SP (rank +
    // spendable shop balance). Keeping the ride is `unlockedVehicles`' job now.
    if (typeof data.lifetimePoints !== 'number' || !Number.isFinite(data.lifetimePoints)) {
      const earned = Object.values(data.cities).reduce(
        (sum, c) => sum + pointsForPlace(c.bestPlace),
        0,
      );
      data.lifetimePoints = Math.max(earned, legacyUnlockThresholdFor(data.settings.playerVehicle));
    }
    // M79: shop fields — same typeof-guard pattern as lifetimePoints (a stored
    // 0 survives; a pre-M79 blob backfills). spentPoints can never exceed the
    // career total (a corrupt blob would show a negative balance forever).
    if (typeof data.spentPoints !== 'number' || !Number.isFinite(data.spentPoints)) {
      data.spentPoints = 0;
    }
    // M130: bought SP — same typeof guard; bounded (the server caps a pack at
    // 100k SP and a device can only ever mirror what the account holds).
    if (typeof data.purchasedPoints !== 'number' || !Number.isFinite(data.purchasedPoints)) {
      data.purchasedPoints = 0;
    }
    data.purchasedPoints = Math.min(Math.max(0, Math.floor(data.purchasedPoints)), 1_000_000);
    // spent can never exceed everything that was ever spendable (career +
    // bought) — a corrupt blob would otherwise show a negative balance forever.
    data.spentPoints = Math.min(
      Math.max(0, data.spentPoints),
      data.lifetimePoints + data.purchasedPoints,
    );
    if (
      typeof data.offlineTourBonusPaid !== 'number' ||
      !Number.isFinite(data.offlineTourBonusPaid)
    ) {
      data.offlineTourBonusPaid = 0;
    }
    // Cosmetics: keep only KNOWN catalog ids (a removed/typo'd item must never
    // crash rendering), and a loadout may only wear items that are owned.
    const rawCos = (data as { cosmetics?: unknown }).cosmetics as CosmeticsSave | undefined;
    const owned = Array.isArray(rawCos?.owned)
      ? rawCos.owned.filter((id) => typeof id === 'string' && isCosmeticId(id))
      : [];
    const loadouts: Record<string, VehicleLoadout> = {};
    if (rawCos?.loadouts && typeof rawCos.loadouts === 'object') {
      for (const [veh, raw] of Object.entries(rawCos.loadouts)) {
        // M109: the key must be a REAL ride. Nothing validated it before, so a
        // corrupt (or server-fed) blob could carry unbounded junk keys that
        // then rode the garage push straight back up.
        if (!(VEHICLE_ROSTER as readonly string[]).includes(veh)) continue;
        if (!raw || typeof raw !== 'object') continue;
        const src = raw as VehicleLoadout;
        const out: VehicleLoadout = {};
        for (const key of ['paint', 'wrap', 'tires'] as const) {
          const id = src[key];
          if (typeof id === 'string' && owned.includes(id) && cosmeticById(id)?.category === key) {
            out[key] = id;
          }
        }
        // M128: mods — any number, owned + really mods, deduped, bounded.
        if (Array.isArray(src.mods)) {
          const mods = [...new Set(src.mods)].filter(
            (id): id is string =>
              typeof id === 'string' && owned.includes(id) && cosmeticById(id)?.category === 'mod',
          );
          if (mods.length > 0) out.mods = mods.slice(0, 16);
        }
        if (isKitNumber(src.kitNumber)) out.kitNumber = src.kitNumber;
        if (isTifoStyle(src.tifoStyle)) out.tifoStyle = src.tifoStyle; // M128c: the cape's design
        // M87: kit paints + flag wraps are ONE merged "Paint Jobs" slot now —
        // a car wears one or the other. Pre-M87 saves could hold both (buying
        // auto-equipped, and the wrap silently won on the body while the shop
        // still said the paint was EQUIPPED — the owner's reported bug). Keep
        // the KIT: it's the one that was invisibly losing.
        if (out.paint && out.wrap) delete out.wrap;
        if (Object.keys(out).length > 0) loadouts[veh] = out;
      }
    }
    data.cosmetics = { owned, loadouts };
    // M82: weekly Skill-Point counter — backfill pre-M82 blobs / scrub garbage
    // (a corrupt points value must never poison the weekly board).
    const rawWeekly = (data as { weekly?: unknown }).weekly as
      | { key?: unknown; points?: unknown }
      | undefined;
    data.weekly = {
      key: typeof rawWeekly?.key === 'string' ? rawWeekly.key : '',
      points:
        typeof rawWeekly?.points === 'number' && Number.isFinite(rawWeekly.points)
          ? Math.max(0, rawWeekly.points)
          : 0,
    };
    // M100 RACER ID fields — same typeof-guard/backfill pattern as the rest.
    // Everything is clamped so a corrupt blob can only ever UNDER-claim.
    const rawAccount = (data as { account?: unknown }).account as
      | { userId?: unknown }
      | null
      | undefined;
    data.account =
      rawAccount && typeof rawAccount === 'object' && typeof rawAccount.userId === 'string'
        ? { userId: rawAccount.userId.slice(0, 64) }
        : null;
    data.accountImported = (data as { accountImported?: unknown }).accountImported === true;
    // M103: pre-M103 blobs backfill from the live link (a linked device's
    // last account IS its current one); long-signed-out devices read null —
    // their next sign-in reads as a fresh link, exactly the old behavior.
    const rawLast = (data as { lastAccountId?: unknown }).lastAccountId;
    data.lastAccountId =
      typeof rawLast === 'string' && rawLast !== ''
        ? rawLast.slice(0, 64)
        : (data.account?.userId ?? null);
    const rawPot = (data as { guestPoints?: unknown }).guestPoints;
    data.guestPoints =
      typeof rawPot === 'number' && Number.isFinite(rawPot)
        ? Math.min(Math.max(0, Math.round(rawPot)), GUEST_POT_CAP)
        : 0;
    // M121 offline grace. Strict `=== true` so any garbage (and every
    // pre-M121 save) reads as "not cached", never as free ownership.
    data.ownsGameCached = (data as { ownsGameCached?: unknown }).ownsGameCached === true;
    // M123 teaser-race counter — the guestPoints idiom exactly: typeof guard
    // (a stored 0 survives), round, clamp into [0, cap]; garbage / pre-M123
    // blobs backfill 0 (a fresh device has its full free allowance).
    const rawTeaser = (data as { teaserRaces?: unknown }).teaserRaces;
    data.teaserRaces =
      typeof rawTeaser === 'number' && Number.isFinite(rawTeaser)
        ? Math.min(Math.max(0, Math.round(rawTeaser)), TEASER_RACES_CAP)
        : 0;
    const AWARD_KINDS = ['race', 'tour_finale_offline', 'tour_finale_online'];
    const rawAwards = (data as { pendingAwards?: unknown }).pendingAwards;
    data.pendingAwards = (Array.isArray(rawAwards) ? rawAwards : [])
      .filter(
        (a): a is PendingAward =>
          !!a &&
          typeof a === 'object' &&
          typeof (a as PendingAward).id === 'string' &&
          (a as PendingAward).id.length >= 1 &&
          (a as PendingAward).id.length <= 80 &&
          AWARD_KINDS.includes((a as PendingAward).kind) &&
          Number.isInteger((a as PendingAward).place) &&
          (a as PendingAward).place >= 1 &&
          (a as PendingAward).place <= 8 &&
          typeof (a as PendingAward).points === 'number' &&
          Number.isFinite((a as PendingAward).points) &&
          typeof (a as PendingAward).week === 'string',
      )
      .slice(0, PENDING_AWARDS_CAP);
    const rawPending = (data as { pendingPurchases?: unknown }).pendingPurchases;
    data.pendingPurchases = [
      ...new Set(
        (Array.isArray(rawPending) ? rawPending : []).filter(
          (id): id is string => typeof id === 'string' && isCosmeticId(id),
        ),
      ),
    ].slice(0, PENDING_PURCHASES_CAP);
    // M109: unsynced garage changes. Pre-M109 blobs backfill FALSE — their
    // loadouts are whatever the device had, and the first sync should adopt
    // the account's garage rather than let an arbitrary device win by default.
    data.garageDirty = (data as { garageDirty?: unknown }).garageDirty === true;
    // M114: the first-win difficulty CTA. Typeof guard so a stored FALSE is
    // never re-derived; a pre-M114 blob (field absent or corrupt) backfills
    // TRUE when any city record already holds a WIN (bestPlace 1) — existing
    // winners must never be greeted by a "first win" module — else FALSE.
    const rawCta = (data as { difficultyCtaShown?: unknown }).difficultyCtaShown;
    data.difficultyCtaShown =
      typeof rawCta === 'boolean'
        ? rawCta
        : Object.values(data.cities).some(
            (c) => !!c && typeof c === 'object' && (c as CityRecord).bestPlace === 1,
          );
    // M114: the granted-forever vehicle set. Present → validate: known roster
    // ids only, deduped, never 'coupe' (always free — keeping it out keeps the
    // set canonical); garbage drops. ABSENT (every pre-M114 blob, or a corrupt
    // non-array) → SEED from the LEGACY ladder: every vehicle the old
    // thresholds say this career had already earned is granted forever, so the
    // M114 cost raise re-locks nobody. Bounded by the blob's own points under
    // the OLD ladder, so a corrupt blob can only ever reconstruct what a real
    // pre-M114 player could have held — never f1 (not in the legacy table).
    // Runs AFTER the lifetimePoints backfill (the seed reads the final total)
    // and BEFORE the ride coercion below (which honours the grants).
    const rawUnlocked = (data as { unlockedVehicles?: unknown }).unlockedVehicles;
    data.unlockedVehicles = Array.isArray(rawUnlocked)
      ? [
          ...new Set(
            rawUnlocked.filter(
              (id): id is string =>
                typeof id === 'string' &&
                id !== 'coupe' &&
                (VEHICLE_ROSTER as readonly string[]).includes(id),
            ),
          ),
        ]
      : LEGACY_VEHICLE_UNLOCKS_M62.filter((u) => u.points <= data.lifetimePoints).map((u) => u.id);
    // Invariant: the saved ride is ALWAYS unlocked. vehicleParam() (online join
    // URL) and the offline sim read settings.playerVehicle directly, so coercing
    // here — the single load boundary — covers every consumer at once. M114:
    // the shared predicate honours `unlockedVehicles`, so a legacy-granted ride
    // (e.g. a motorcycle earned at the old 275 SP) is never stripped by the
    // raised thresholds.
    if (
      !vehicleUnlockedBy(data.lifetimePoints, data.unlockedVehicles, data.settings.playerVehicle)
    ) {
      data.settings.playerVehicle = 'coupe';
    }
    // M138: a device that signed out BEFORE sign-out started scrubbing still
    // holds the last account's career — clean it once. "Signed out" =
    // unlinked but with a previous Racer ID on record (an expired session
    // keeps its link, and a never-linked guest has no lastAccountId).
    if ((data as { signOutScrubbed?: unknown }).signOutScrubbed !== true) {
      const scrubbed =
        data.account === null && data.lastAccountId !== null ? toGuestSave(data) : data;
      scrubbed.signOutScrubbed = true;
      return scrubbed;
    }
    return data;
  } catch {
    return defaultSave();
  }
}

export function loadSave(): SaveData {
  try {
    return parseSave(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return defaultSave(); // storage blocked (private mode) — play with an in-memory save
  }
}

export function storeSave(save: SaveData): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(save));
  } catch {
    // Storage full/blocked: the game still works, progress just won't persist.
  }
}

export function clearSave(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
